import { AnimatePresence, motion } from 'framer-motion'
import {
    AlertTriangle,
    BarChart2,
    ChevronRight,
    Clock,
    History,
    Loader2,
    Mail,
    Send,
    Settings,
    Users,
    Zap,
} from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './DRDashboard.module.css'

/* ── helpers ──────────────────────────────────────────── */
const fmt12 = (dateStr) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

const relativeDay = (dateStr) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    const now = new Date()
    const diffDays = Math.floor((now - d) / 86400000)
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const fmtDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const STATUS_CHIP = {
    sent:    { label: 'Sent ✓',   cls: 'chipSent' },
    partial: { label: 'Partial',  cls: 'chipPartial' },
    failed:  { label: 'Failed ✗', cls: 'chipFailed' },
}

/* ── Skeleton pieces ──────────────────────────────────── */
const SkeletonBar = ({ w = '100%', h = 14, mb = 0 }) => (
    <div className={styles.skeletonBar} style={{ width: w, height: h, marginBottom: mb }} />
)

const DRDashboard = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return asCtx?.activeStore && asCtx.activeStore.trim() !== '' ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const navigate = useNavigate()

    /* ── state ── */
    const [statusData, setStatusData]       = useState(null)
    const [receivers, setReceivers]         = useState([])
    const [history, setHistory]             = useState([])
    const [loadingStatus, setLoadingStatus] = useState(true)
    const [loadingRecv, setLoadingRecv]     = useState(true)
    const [loadingHist, setLoadingHist]     = useState(true)
    const [isSending, setIsSending]         = useState(false)

    /* ── API calls ── */
    const getManualStatus = async () => {
        setLoadingStatus(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetManualStatus', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') setStatusData(data?.response)
            else if (data?.message === 'tokenExpired') authCtx.logout()
        } catch { /* silent */ } finally { setLoadingStatus(false) }
    }

    const getReceivers = async () => {
        setLoadingRecv(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetReceivers', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') setReceivers(data?.response?.receivers ?? [])
            else if (data?.message === 'tokenExpired') authCtx.logout()
        } catch { /* silent */ } finally { setLoadingRecv(false) }
    }

    const getRecentHistory = async () => {
        setLoadingHist(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetHistory', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, limit: 4 }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') setHistory(data?.response?.history ?? [])
            else if (data?.message === 'tokenExpired') authCtx.logout()
        } catch { /* silent */ } finally { setLoadingHist(false) }
    }

    const sendNow = async () => {
        if (isSending) return
        setIsSending(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsSendNow', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, authIdentifier : authCtx?.authIdentifier, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) { toast.error('Something went wrong.'); return }
            const data = await res.json()
            if (data?.status === 'success') {
                toast.success('Report sent!')
                setStatusData(prev => prev ? {
                    ...prev,
                    usedToday: (prev.usedToday ?? 0) + 1,
                    canSend: ((prev.usedToday ?? 0) + 1) < (prev.maxAllowed ?? 3),
                } : prev)
                getRecentHistory()
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            } else if (data?.message === 'nodeServiceUnavailable') {
                toast.error('Report service is temporarily unavailable. Please try again in a few minutes.')
            } else if (data?.message === 'limitReached') {
                toast.error('Daily send limit reached. Resets at midnight.')
                setStatusData(prev => prev ? { ...prev, canSend: false } : prev)
            } else {
                toast.error('Failed to send report.')
            }
        } catch { toast.error('Something went wrong.') } finally {
            setIsSending(false)
        }
    }

    useEffect(() => {
        getManualStatus()
        getReceivers()
        getRecentHistory()
    }, [activeStore?.storeIdentifier])

    /* ── derived ── */
    const activeReceivers  = receivers.filter(r => r.isActive === 1)
    const emailReceivers   = activeReceivers.filter(r => r.deliveryPlatform === 'E-mail' || r.deliveryPlatform === 'email')
    const waReceivers      = activeReceivers.filter(r => r.deliveryPlatform === 'WhatsApp')
    const isFirstTime      = !loadingRecv && receivers.length === 0
    const autoOn           = statusData?.autoReportEnabled === 1
    const canSend          = statusData?.canSend !== false
    const usedToday        = statusData?.usedToday ?? 0
    const maxAllowed       = statusData?.maxAllowed ?? 3
    const remaining        = statusData?.remaining ?? Math.max(0, maxAllowed - usedToday)

    /* ── quota line ── */
    const quotaLine = !loadingStatus
        ? remaining === 0
            ? `Daily limit reached (${usedToday}/${maxAllowed}) · Resets at midnight`
            : `${usedToday} of ${maxAllowed} manual sends used today · ${remaining} remaining · resets at midnight`
        : null

    /* ── auto send card state ── */
    const autoCardValue = () => {
        if (!autoOn) return { val: 'Not scheduled', sub: 'Auto-report is off', valCls: '' }
        if (statusData?.autoSentTonight) return { val: 'Sent', sub: `${fmt12(statusData?.autoSentAt) ?? ''}`, valCls: styles.valGreen }
        if (statusData?.autoFailedTonight) return { val: 'Failed', sub: statusData?.autoRetryEnabled ? 'Auto-retry enabled' : 'No retry scheduled', valCls: styles.valRed }
        return { val: 'Pending', sub: `Scheduled · ${statusData?.deliveryWindow ?? '10:00 PM'}`, valCls: '' }
    }
    const autoCard = autoCardValue()

    /* ── 7-day delivery ── */
    const deliveryRate = (() => {
        if (!history.length) return { val: '–', sub: 'No data yet' }
        const last7 = history.slice(0, 7)
        const attempted = last7.reduce((a, r) => a + (r.totalRecipients ?? 0), 0)
        const sent      = last7.reduce((a, r) => a + (r.totalSent ?? 0), 0)
        if (!attempted) return { val: '–', sub: 'No data yet' }
        const pct = Math.round((sent / attempted) * 100)
        return { val: `${pct}%`, sub: pct === 100 ? 'All recipients reached' : 'Some failures this week' }
    })()

    /* ── empty / first-time ── */
    if (!loadingRecv && !loadingStatus && isFirstTime) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.emptyPage}>
                    <div className={styles.emptyIllustration}>
                        <BarChart2 size={52} color="#6C3FC5" />
                        <Mail size={28} color="#a78bfa" className={styles.emptyMailIcon} />
                    </div>
                    <h2 className={styles.emptyTitle}>Get daily business reports</h2>
                    <p className={styles.emptyBody}>
                        Stay on top of your salon's performance.<br />
                        Receive collection, attendance and booking summaries every night automatically.
                    </p>
                    <Link to="/config/daily-reports" className={styles.primaryBtn}>
                        Set up daily reports
                    </Link>
                    <button className={styles.ghostBtn} type="button">Learn more ↗</button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.wrapper}>
            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <p className={styles.breadcrumb}>Lokaci Pro</p>
                    <h1 className={styles.pageTitle}>Daily reports</h1>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.headerBtn} onClick={() => navigate('/reports/daily/settings')} type="button">
                        <Settings size={14} /> Settings
                    </button>
                    <button className={styles.headerBtn} onClick={() => navigate('/reports/daily/history')} type="button">
                        <History size={14} /> History
                    </button>
                </div>
            </div>

            {/* ── Send Now Hero Card ── */}
            <div className={styles.heroCard}>
                <div className={styles.heroLeft}>
                    {loadingStatus ? (
                        <SkeletonBar w={160} h={12} mb={10} />
                    ) : (
                        <div className={styles.autoStatusPill}>
                            <span className={`${styles.dot} ${autoOn ? styles.dotGreen : styles.dotGrey}`} />
                            {autoOn
                                ? `Auto-report active · ${statusData?.deliveryWindow ?? '10:00 PM'} window`
                                : 'Auto-report is off'}
                        </div>
                    )}
                    <h2 className={styles.heroTitle}>Send today's report now</h2>
                    {loadingStatus
                        ? <SkeletonBar w={280} h={11} />
                        : <p className={styles.heroSub}>{quotaLine}</p>}
                </div>
                <div className={styles.heroRight}>
                    <button
                        className={`${styles.sendBtn} ${(!canSend || isSending || isFirstTime) ? styles.sendBtnDisabled : ''}`}
                        onClick={sendNow}
                        disabled={!canSend || isSending || isFirstTime}
                        title={!canSend ? `You've used all ${maxAllowed} manual sends for today.` : isFirstTime ? 'Add a receiver first' : undefined}
                        type="button"
                    >
                        {isSending
                            ? <><Loader2 size={15} className={styles.spinIcon} /> Sending…</>
                            : <><Send size={15} /> Send report now</>}
                    </button>
                </div>
            </div>

            {/* ── Stats Strip ── */}
            <div className={styles.statsGrid}>
                {/* Card 1 — Today's auto send */}
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Today's Auto Send</div>
                    {loadingStatus
                        ? <><SkeletonBar w="60%" h={22} mb={6} /><SkeletonBar w="80%" h={11} /></>
                        : <>
                            <div className={`${styles.statValue} ${autoCard.valCls}`}>{autoCard.val}</div>
                            <div className={styles.statSub}>{autoCard.sub}</div>
                        </>}
                </div>

                {/* Card 2 — Last sent */}
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Last Sent</div>
                    {loadingStatus
                        ? <><SkeletonBar w="55%" h={22} mb={6} /><SkeletonBar w="70%" h={11} /></>
                        : statusData?.lastSentAt
                            ? <>
                                <div className={styles.statValue}>{relativeDay(statusData.lastSentAt)}</div>
                                <div className={styles.statSub}>{fmt12(statusData.lastSentAt)} · {statusData?.lastTriggerSource ?? 'Auto'}</div>
                            </>
                            : <>
                                <div className={styles.statValue}>Never</div>
                                <div className={styles.statSub}>No reports sent yet</div>
                            </>}
                </div>

                {/* Card 3 — 7-day delivery */}
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>7-Day Delivery</div>
                    {loadingHist
                        ? <><SkeletonBar w="40%" h={22} mb={6} /><SkeletonBar w="75%" h={11} /></>
                        : <>
                            <div className={styles.statValue}>{deliveryRate.val}</div>
                            <div className={styles.statSub}>{deliveryRate.sub}</div>
                        </>}
                </div>

                {/* Card 4 — Receivers */}
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Receivers</div>
                    {loadingRecv
                        ? <><SkeletonBar w="30%" h={22} mb={6} /><SkeletonBar w="65%" h={11} /></>
                        : activeReceivers.length === 0
                            ? <>
                                <div className={`${styles.statValue} ${styles.valWarn}`}>None set up</div>
                                <div className={styles.statSub}>Add receivers to get started</div>
                            </>
                            : <>
                                <div className={styles.statValue}>{activeReceivers.length}</div>
                                <div className={styles.statSub}>
                                    {emailReceivers.length > 0 ? `${emailReceivers.length} email` : ''}
                                    {emailReceivers.length > 0 && waReceivers.length > 0 ? ' · ' : ''}
                                    {waReceivers.length > 0 ? `${waReceivers.length} WhatsApp` : ''}
                                </div>
                            </>}
                </div>
            </div>

            {/* ── Two-column content row ── */}
            <div className={styles.contentRow}>
                {/* Left — Delivery channels */}
                <div className={styles.contentCard}>
                    <div className={styles.contentCardHeader}>
                        <span className={styles.contentCardTitle}>Delivery channels</span>
                        {!loadingRecv && activeReceivers.length > 0 && (
                            <span className={styles.allActiveCaption}>All active</span>
                        )}
                    </div>

                    {loadingRecv ? (
                        <div className={styles.channelList}>
                            {[1, 2, 3].map(i => (
                                <div key={i} className={styles.channelRowSkeleton}>
                                    <SkeletonBar w={24} h={24} />
                                    <div style={{ flex: 1 }}>
                                        <SkeletonBar w="70%" h={12} mb={5} />
                                        <SkeletonBar w="40%" h={10} />
                                    </div>
                                    <SkeletonBar w={36} h={18} />
                                </div>
                            ))}
                        </div>
                    ) : activeReceivers.length === 0 ? (
                        <div className={styles.channelEmpty}>
                            <AlertTriangle size={20} color="#f59e0b" />
                            <p>No receivers configured yet. Add your first receiver to start getting reports.</p>
                            <Link to="/config/daily-reports" className={styles.channelEmptyLink}>Add receiver →</Link>
                        </div>
                    ) : (
                        <div className={styles.channelList}>
                            {activeReceivers.slice(0, 4).map((r, i) => {
                                const isWA = r.deliveryPlatform === 'WhatsApp'
                                const addr = `${r.countryCode && r.countryCode !== 'null' ? r.countryCode + ' ' : ''}${r.receiver}`
                                return (
                                    <div key={i} className={styles.channelRow}>
                                        <div className={styles.channelIcon}>
                                            {isWA
                                                ? <FaWhatsapp size={15} color="#25D366" />
                                                : <Mail size={15} color="#2463EB" />}
                                        </div>
                                        <div className={styles.channelInfo}>
                                            <span className={styles.channelAddr}>{addr}</span>
                                            <span className={styles.channelRole}>{r.receiverRole}</span>
                                        </div>
                                        <div className={`${styles.miniToggle} ${r.isActive === 1 ? styles.miniToggleOn : ''}`} />
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    <div className={styles.contentCardFooter}>
                        {activeReceivers.length > 4
                            ? <Link to="/config/daily-reports" className={styles.viewAllLink}>View all {activeReceivers.length} receivers →</Link>
                            : <Link to="/config/daily-reports" className={styles.manageBtn}>
                                <Users size={13} /> Manage receivers
                            </Link>}
                    </div>
                </div>

                {/* Right — Recent sends */}
                <div className={styles.contentCard}>
                    <div className={styles.contentCardHeader}>
                        <span className={styles.contentCardTitle}>Recent sends</span>
                        {!loadingHist && history.length > 0 && (
                            <Link to="/reports/daily/history" className={styles.viewAllLink}>View all</Link>
                        )}
                    </div>

                    {loadingHist ? (
                        <div className={styles.historyList}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className={styles.historyRowSkeleton}>
                                    <div style={{ flex: 1 }}>
                                        <SkeletonBar w="50%" h={12} mb={5} />
                                        <SkeletonBar w="40%" h={10} />
                                    </div>
                                    <SkeletonBar w={52} h={20} />
                                </div>
                            ))}
                        </div>
                    ) : history.length === 0 ? (
                        <div className={styles.historyEmpty}>
                            <p>No reports sent yet. Hit <strong>Send report now</strong> to send your first one.</p>
                        </div>
                    ) : (
                        <div className={styles.historyList}>
                            {history.slice(0, 4).map((r, i) => {
                                const chip = r.status
                                    ? STATUS_CHIP[r.status] ?? { label: r.status, cls: 'chipSent' }
                                    : ((r.totalFailed ?? 0) > 0 ? STATUS_CHIP.partial : STATUS_CHIP.sent)
                                return (
                                    <div
                                        key={i}
                                        className={styles.historyRow}
                                        onClick={() => navigate('/reports/daily/history', { state: { openReport: r.reportIdentifier } })}
                                    >
                                        <div className={styles.historyInfo}>
                                            <span className={styles.historyDate}>{fmtDate(r.reportDate ?? r.createdOn)}</span>
                                            <span className={styles.historySub}>{fmt12(r.createdOn)} · {r.triggerSource}</span>
                                        </div>
                                        <span className={`${styles.chip} ${styles[chip.cls]}`}>{chip.label}</span>
                                        <ChevronRight size={14} color="#94a3b8" />
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DRDashboard
